/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import MsDyn365 from '@msdyn365-commerce/core';
import * as Msdyn365 from '@msdyn365-commerce/core';

import {
    IContentBlockAdditionalContentItemViewProps,
    IContentBlockAdditionalContentViewProps
} from '@msdyn365-commerce-modules/content-block/src/modules/content-block/./components/additional-content';
import { IContentBlockViewProps } from '@msdyn365-commerce-modules/content-block/src/modules/content-block/./content-block';
import { IContentBlockProps } from '../definition-extensions/content-block.ext.props.autogenerated';

/**
 * Render Additional Content.
 * @param additionalContent - Additional content view props.
 * @returns JSX Element.
 */
const renderAdditionalContent = (additionalContent: IContentBlockAdditionalContentViewProps) => {
    return (
        <Node {...additionalContent.additionalContentNode}>
            {additionalContent.additionalContentItems?.map((item: IContentBlockAdditionalContentItemViewProps) => {
                return (
                    <>
                        {item.heading}
                        <Node {...item.additionalContentItemContainer}>
                            {item.text}
                            <Node {...item.additionalContentItemLinks}>{item.links}</Node>
                        </Node>
                    </>
                );
            })}
        </Node>
    );
};

const ContentBlockView: React.FC<IContentBlockViewProps & IContentBlockProps<{}>> = props => {
    const {
        contentBlockContainer,
        imageContainer,
        detailsContainer,
        title,
        text,
        links,
        image,
        contentBlockAnchorTag,
        imageLink,
        imageAriaLabel,
        additionalContent,
        context: {
            request: {
                user: { isAuthenticated },
                gridSettings
            },
            actionContext: { requestContext }
        },
        config: { showOnlyForGuest, showOnlyForloggedIn, mobileImage, uniqueId }
    } = props;
    const imageJSX = image as JSX.Element;
    const contentImageProps = imageJSX?.props;
    const src = contentImageProps?.src;

    const [isMobile, setIsMobile] = React.useState<boolean>();
    const _updateViewport = (): void => {
        const viewport = _getViewport();
        viewport === 'xs' || viewport === 'sm' ? setIsMobile(true) : setIsMobile(false);
    };
    type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    const _getViewport = (): GridSize => {
        // Always render in mobile viewport on a mobile device
        if (props.context?.request && props.context.request.device && props.context.request.device.Type === 'Mobile') {
            return 'xs';
        }
        if (MsDyn365.isBrowser && window.innerWidth) {
            const gridSettings = props.context?.request.gridSettings;
            if (gridSettings) {
                if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
                    return 'xs';
                } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                    return 'sm';
                } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                    return 'md';
                } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                    return 'lg';
                }
                return 'xl';
            }
        }
        return 'lg';
    };
    React.useEffect(() => {
        _updateViewport();
        if (MsDyn365.isBrowser && window.addEventListener) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            window.addEventListener('resize', _updateViewport);
        }
    }, []);
    const imageProps = {
        gridSettings: gridSettings ?? {},
        imageSettings: mobileImage?.imageSettings
    };
    const mobileFullImage = (
        <Msdyn365.Image
            {...mobileImage}
            {...imageProps}
            requestContext={requestContext}
            editProps={{
                key: mobileImage ?? {},
                requestContext: requestContext,
                moduleType: 'image',
                imagePropertyName: 'image',
                moduleId: props.id
            }}
            shouldSkipToMainImage
        />
    );
    if ((isAuthenticated && showOnlyForloggedIn) || (showOnlyForGuest && !isAuthenticated) || (!showOnlyForGuest && !showOnlyForloggedIn)) {
        if (imageLink) {
            return (
                <Module id={uniqueId} {...contentBlockContainer}>
                    <Node
                        {...contentBlockAnchorTag}
                        href={imageLink}
                        className={contentBlockAnchorTag ? contentBlockAnchorTag.className : ''}
                        aria-label={imageAriaLabel}
                    >
                        {isMobile ? (
                            <Node {...imageContainer}>{mobileImage ? mobileFullImage : image}</Node>
                        ) : (
                            <Node {...imageContainer}>{image}</Node>
                        )}
                    </Node>
                    <Node {...detailsContainer}>
                        {title}
                        {text}
                        {links}
                        {additionalContent && renderAdditionalContent(additionalContent)}
                    </Node>
                </Module>
            );
        }
        return (
            <Module id={uniqueId} {...contentBlockContainer}>
                {isMobile
                    ? src && <Node {...imageContainer}>{mobileImage ? mobileFullImage : image}</Node>
                    : src && <Node {...imageContainer}>{image}</Node>}
                <Node {...detailsContainer}>
                    {title}
                    {text}
                    {links}
                    {additionalContent && renderAdditionalContent(additionalContent)}
                </Node>
            </Module>
        );
    } else {
        return null;
    }
};

export default ContentBlockView;
